import React from 'react';
import { Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useCan from './useCan';
import type { Permission } from '@/permissions_manifest.generated';

interface CanProps {
  requiredPermission: Permission;
  children: React.ReactElement<{ disabled?: boolean; icon?: React.ReactNode }>;
}

/**
 * <Can> — 主畫面 button 的權限 gate
 *
 * 有權限 → render child 原樣。
 * 沒權限 → 強制 disabled + 鎖頭 icon + tooltip 提示。
 *
 * 用法：
 *   <Can requiredPermission="orders:write">
 *     <Button>Edit</Button>
 *   </Can>
 *
 * child 必須接受 `disabled` 與 `icon` props（Ant Design Button / Dropdown.Button 皆支援）。
 * Menu.Item 等「沒權限就不該出現」的情境請改用 <CanShow>。
 */
const Can: React.FC<CanProps> = ({ requiredPermission, children }) => {
  const can = useCan();
  const { t } = useTranslation();

  const child = React.Children.only(children);
  const allowed = can(requiredPermission);

  if (allowed) return child;

  const lockedChild = React.cloneElement(child, {
    disabled: true,
    icon: <LockOutlined />,
  });

  const [resource, action] = String(requiredPermission).split(':');
  const resourceLabel = t(`common.resources.${resource}`, { defaultValue: resource });
  const actionLabel = action
    ? t(`common.permission_actions.${action}`, { defaultValue: action })
    : '';
  const permissionLabel = action ? `${resourceLabel} - ${actionLabel}` : resourceLabel;

  return (
    <Tooltip
      title={
        <>
          <div>{t('shared.permission_guard.message')}</div>
          <div style={{ fontSize: 11, opacity: 0.75 }}>
            {t('shared.permission_guard.detail', { permission: permissionLabel })}
          </div>
        </>
      }
    >
      {lockedChild}
    </Tooltip>
  );
};

export default Can;
